import React, { useMemo } from "react";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { LineChart } from "@mui/x-charts/LineChart";
import CatalogPageShell from "../Products/CatalogPageShell.jsx";
import { catalogPrimaryButtonSx } from "../Products/catalogGridStyles.js";
import useApiData from "../../services/useApiData.js";
import {
    KpiCard,
    KpiGrid,
    ReportContent,
    ReportError,
    ReportLoading,
    ReportScroll,
    ReportSection,
    ReportSplit,
    ReportToolbar,
    SpotlightStrip,
    formatNumber,
    formatPercent,
} from "./ReportPrimitives.jsx";

const SERIES_COLORS = [
    "#0d9488",
    "#0369a1",
    "#7c3aed",
    "#db2777",
    "#ea580c",
    "#ca8a04",
    "#16a34a",
    "#0f766e",
    "#4f46e5",
    "#be123c",
];

function chartDayLabel(ymd) {
    const [year, month, day] = String(ymd || "")
        .split("-")
        .map(Number);
    if (!year || !month || !day) return ymd || "";
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.toLocaleDateString("en-US", {
        month: "numeric",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
    });
}

function skuLabel(row) {
    return (
        String(row?.listingField || "").trim() ||
        String(row?.itemName || "").trim() ||
        String(row?.internalSkuId || "").trim() ||
        "Untitled SKU"
    );
}

export default function ReportingResearch() {
    const { data, error, load, refetch } = useApiData(
        "api/broylesllc/reports/research-scorecard"
    );

    const kpis = data?.kpis;
    const rates = data?.captureRates;
    const emptyCiphers = data?.emptyCiphers;
    const tracker = data?.newSkuTracker;
    const todayList = data?.newSkusToday || [];
    const newSkuTarget = data?.newSkuTarget ?? 20;

    const chartLabels = useMemo(
        () => (tracker?.days || []).map((day) => chartDayLabel(day)),
        [tracker?.days]
    );

    const chartSeries = useMemo(
        () =>
            (tracker?.series || []).map((series, index) => ({
                id: series.name,
                label: series.name,
                data: series.points || [],
                color: SERIES_COLORS[index % SERIES_COLORS.length],
                showMark: false,
                curve: "linear",
            })),
        [tracker?.series]
    );

    const spotlightItems = [
        {
            label: "Captured SKU Count",
            value: formatNumber(kpis?.captured, 0),
        },
        {
            label: "Finalized SKU Count",
            value: formatNumber(kpis?.finalized, 0),
        },
        {
            label: "New SKUs Today",
            value: formatNumber(kpis?.newSkusToday, 0),
            hint: `Target - ${newSkuTarget}+`,
        },
    ];

    return (
        <CatalogPageShell
            title="Research Scorecard"
            subtitle="SKU research completeness, new-SKU volume, and cipher gaps from the Product collection."
        >
            <ReportScroll>
                <ReportToolbar>
                    <Button
                        type="button"
                        variant="contained"
                        size="small"
                        disabled={load}
                        onClick={() => refetch?.()}
                        startIcon={
                            load ? <CircularProgress size={14} color="inherit" /> : null
                        }
                        sx={catalogPrimaryButtonSx}
                    >
                        {load ? "Refreshing…" : "Refresh"}
                    </Button>
                    <div className="report-toolbar-spacer" />
                    <div className="report-toolbar-meta">
                        {data?.today
                            ? `As of ${chartDayLabel(data.today)} (${data.timezone || "America/New_York"})`
                            : "Trailing 30 days + today"}
                    </div>
                </ReportToolbar>

                {error ? <ReportError message="Failed to load research scorecard." /> : null}

                {load && !data ? (
                    <ReportLoading label="Crunching research scorecard…" />
                ) : data ? (
                    <ReportContent>
                        <SpotlightStrip items={spotlightItems} />

                        <ReportSplit>
                            <ReportSection
                                eyebrow="Volume"
                                title="New SKU Tracker: 30 Days"
                                subtitle="Daily new Products by Created By"
                            >
                                <div className="report-panel">
                                    <div className="report-panel-body">
                                        {chartSeries.length && chartLabels.length ? (
                                            <div className="report-chart">
                                                <LineChart
                                                    height={300}
                                                    series={chartSeries}
                                                    xAxis={[
                                                        {
                                                            data: chartLabels,
                                                            scaleType: "point",
                                                        },
                                                    ]}
                                                    margin={{
                                                        left: 50,
                                                        right: 16,
                                                        top: 20,
                                                        bottom: 40,
                                                    }}
                                                    slotProps={{ legend: { hidden: false } }}
                                                />
                                            </div>
                                        ) : (
                                            <p className="report-chart-empty">
                                                No new SKUs in the last 30 days.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </ReportSection>

                            <ReportSection
                                eyebrow="Today"
                                title="New SKUs Today"
                                subtitle={`${formatNumber(todayList.length, 0)} listed`}
                            >
                                <div className="report-panel">
                                    <div className="report-panel-body">
                                        {todayList.length ? (
                                            <ul className="report-simple-list">
                                                {todayList.map((row) => (
                                                    <li key={row.id}>
                                                        <span className="report-simple-list-primary">
                                                            {skuLabel(row)}
                                                        </span>
                                                        <span className="report-simple-list-meta">
                                                            {row.createdBy}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className="report-chart-empty">
                                                No SKUs created today yet.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </ReportSection>
                        </ReportSplit>

                        <ReportSection
                            eyebrow="Completeness"
                            title="Capture Rates"
                            subtitle="Share of Captured SKUs with each research field filled"
                        >
                            <KpiGrid columns={4}>
                                <KpiCard
                                    label="Capture Rate: ASIN"
                                    value={formatPercent(rates?.asin)}
                                    tone="accent"
                                />
                                <KpiCard
                                    label="Capture Rate: Photos"
                                    value={formatPercent(rates?.photos)}
                                    tone="accent"
                                />
                                <KpiCard
                                    label="Capture Rate: UPCs"
                                    value={formatPercent(rates?.upcs)}
                                    tone="accent"
                                />
                                <KpiCard
                                    label="Capture Rate: Amazon URLs"
                                    value={formatPercent(rates?.amazonUrls)}
                                    tone="accent"
                                />
                                <KpiCard
                                    label="Capture Rate: Support Photos"
                                    value={formatPercent(rates?.supportPhotos)}
                                    tone="accent"
                                />
                                <KpiCard
                                    label="Capture Rate: Models"
                                    value={formatPercent(rates?.models)}
                                    tone="accent"
                                />
                                <KpiCard
                                    label="Capture Rate: MSRPs"
                                    value={formatPercent(rates?.msrps)}
                                    tone="accent"
                                />
                                <KpiCard
                                    label="Capture Rate: Walmart URLs"
                                    value={formatPercent(rates?.walmartUrls)}
                                    tone="accent"
                                />
                            </KpiGrid>
                        </ReportSection>

                        <ReportSection
                            eyebrow="Gaps"
                            title="Empty Ciphers"
                            subtitle="Products missing a dimension link or linked to a blank cipher"
                        >
                            <KpiGrid columns={4}>
                                <KpiCard
                                    label="Empty Brand Ciphers"
                                    value={formatNumber(emptyCiphers?.brand, 0)}
                                />
                                <KpiCard
                                    label="Empty Size Ciphers"
                                    value={formatNumber(emptyCiphers?.size, 0)}
                                />
                                <KpiCard
                                    label="Empty Color Ciphers"
                                    value={formatNumber(emptyCiphers?.color, 0)}
                                />
                                <KpiCard
                                    label="Empty Model Ciphers"
                                    value={formatNumber(emptyCiphers?.model, 0)}
                                />
                            </KpiGrid>
                        </ReportSection>
                    </ReportContent>
                ) : null}
            </ReportScroll>
        </CatalogPageShell>
    );
}
